import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, FileSpreadsheet, FileText, Receipt, Send } from "lucide-react";
import { BarChart, Bar, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend } from "recharts";
import { api, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EmptyState, KPICard, PageHeader, StatusPill } from "@/components/uiext";
import { MyPlanCard } from "@/components/MyPlanCard";
import { toast } from "sonner";

function firstOfMonth() {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-01`;
}
function todayISO() {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(
    d.getUTCDate()
  ).padStart(2, "0")}`;
}

function downloadWithToken(url, filename) {
  const token = localStorage.getItem("elswitch_token");
  // fetch + blob so we can attach Authorization header
  fetch(url, { headers: { Authorization: `Bearer ${token}` } })
    .then(async (r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const a = document.createElement("a");
      const href = URL.createObjectURL(blob);
      a.href = href;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
    })
    .catch((e) => toast.error(`Download failed: ${e.message || e}`));
}

export default function BillingPage() {
  const { t } = useTranslation();
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(todayISO());
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reportDay, setReportDay] = useState(1);
  const [sending, setSending] = useState(false);
  const [mailLog, setMailLog] = useState([]);

  const load = () => {
    setLoading(true);
    api
      .get(`/billing/summary?from=${from}&to=${to}`)
      .then((r) => setSummary(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load billing"))
      .finally(() => setLoading(false));
  };

  const loadLog = () => {
    api.get("/billing/email-log?limit=20").then((r) => setMailLog(r.data || [])).catch(() => {});
  };

  useEffect(() => {
    load();
    loadLog();
    api.get("/settings/billing").then((r) => setReportDay(r.data.report_day_of_month || 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveReportDay = async (d) => {
    const day = Math.max(1, Math.min(28, Number(d) || 1));
    setReportDay(day);
    try {
      await api.put("/settings/billing", { report_day_of_month: day });
      toast.success(`Monthly report day set to ${day}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/billing/send-report", { from, to });
      toast.success(t("billing.sentOk", { id: data?.provider_id || "" }));
      loadLog();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("billing.sendFailed"));
    } finally {
      setSending(false);
    }
  };

  const chartData = useMemo(() => {
    if (!summary?.rows) return [];
    return summary.rows.map((r) => ({
      date: r.date.slice(5),
      peak: r.peak_channels,
      minutes: r.minutes,
    }));
  }, [summary]);

  const totals = summary?.totals || { calls: 0, minutes: 0, max_peak_channels: 0, days: 0 };

  return (
    <div data-testid="page-billing">
      <PageHeader
        title="Billing"
        subtitle="Peak concurrent channels, talk minutes, and downloadable reports for your organization."
        icon={Receipt}
        actions={
          <>
            <Button
              variant="outline"
              onClick={() =>
                downloadWithToken(
                  `${API_BASE}/billing/report.csv?from=${from}&to=${to}`,
                  `elswitch-${from}_${to}.csv`
                )
              }
              data-testid="billing-download-csv"
            >
              <FileSpreadsheet className="h-4 w-4 mr-1" /> CSV
            </Button>
            <Button
              variant="outline"
              onClick={() =>
                downloadWithToken(
                  `${API_BASE}/billing/report.pdf?from=${from}&to=${to}`,
                  `elswitch-${from}_${to}.pdf`
                )
              }
              data-testid="billing-download-pdf"
            >
              <FileText className="h-4 w-4 mr-1" /> PDF
            </Button>
            <Button onClick={sendNow} disabled={sending} data-testid="billing-send-now">
              <Send className="h-4 w-4 mr-1" />
              {sending ? t("billing.sending") : t("billing.sendNow")}
            </Button>
          </>
        }
      />

      <div className="mb-4">
        <MyPlanCard />
      </div>

      <div className="grid md:grid-cols-[1fr_260px] gap-4 mb-4">
        <Card>
          <CardContent className="p-4 flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[160px]">
              <Label className="text-xs">From</Label>
              <Input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                data-testid="billing-from"
              />
            </div>
            <div className="flex-1 min-w-[160px]">
              <Label className="text-xs">To</Label>
              <Input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                data-testid="billing-to"
              />
            </div>
            <Button onClick={load} disabled={loading} data-testid="billing-apply">
              {loading ? "Loading…" : "Apply"}
            </Button>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 space-y-2">
            <div className="text-xs text-muted-foreground">Monthly report day (1–28)</div>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min={1}
                max={28}
                value={reportDay}
                onChange={(e) => setReportDay(e.target.value)}
                className="w-20"
                data-testid="billing-report-day"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => saveReportDay(reportDay)}
                data-testid="billing-save-day"
              >
                Save
              </Button>
            </div>
            <div className="text-[11px] text-muted-foreground">
              Day of month when the automated report will be emailed (email delivery coming soon).
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <KPICard
          label="Total calls"
          value={totals.calls}
          icon={Receipt}
          testid="kpi-billing-calls"
        />
        <KPICard
          label="Total minutes"
          value={totals.minutes}
          icon={Receipt}
          testid="kpi-billing-minutes"
        />
        <KPICard
          label="Peak channels"
          value={`${totals.max_peak_channels}${
            summary?.plan_limit ? ` / ${summary.plan_limit}` : ""
          }`}
          icon={Receipt}
          testid="kpi-billing-peak"
        />
        <KPICard
          label="Plan"
          value={
            summary?.plan ? (
              <span className="text-base font-semibold">
                <StatusPill kind="info">{summary.plan}</StatusPill>
              </span>
            ) : (
              "—"
            )
          }
          icon={Receipt}
          testid="kpi-billing-plan"
        />
      </div>

      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="text-sm font-medium mb-3">Daily peak channels &amp; minutes</div>
          {chartData.length === 0 ? (
            <EmptyState title="No data yet" description="Once calls flow, daily peaks appear here." />
          ) : (
            <div style={{ width: "100%", height: 240 }}>
              <ResponsiveContainer>
                <BarChart data={chartData} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" fontSize={11} stroke="hsl(var(--muted-foreground))" />
                  <YAxis yAxisId="l" fontSize={11} stroke="hsl(var(--muted-foreground))" />
                  <YAxis yAxisId="r" orientation="right" fontSize={11} stroke="hsl(var(--muted-foreground))" />
                  <Tooltip
                    contentStyle={{
                      background: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      fontSize: 12,
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar yAxisId="l" dataKey="peak" name="Peak channels" fill="hsl(var(--accent))" />
                  <Bar yAxisId="r" dataKey="minutes" name="Minutes" fill="hsl(var(--info))" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader className="sticky-table-head">
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Peak channels</TableHead>
                <TableHead className="text-right">Calls</TableHead>
                <TableHead className="text-right">Minutes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(summary?.rows || []).map((r) => (
                <TableRow key={r.date} className="row-hover" data-testid={`billing-row-${r.date}`}>
                  <TableCell className="font-mono text-xs">{r.date}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.peak_channels}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.calls}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.minutes.toFixed(1)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <Card className="mt-4">
        <CardContent className="p-0">
          <div className="px-4 py-3 border-b border-border text-sm font-medium flex items-center gap-2">
            <Send className="h-4 w-4 text-accent" /> {t("billing.recentSends")}
          </div>
          {mailLog.length === 0 ? (
            <div className="px-4 py-8 text-sm text-muted-foreground text-center">
              {t("billing.recentSendsEmpty")}
            </div>
          ) : (
            <Table>
              <TableHeader className="sticky-table-head">
                <TableRow>
                  <TableHead>{t("billing.mailCols.when")}</TableHead>
                  <TableHead>{t("billing.mailCols.status")}</TableHead>
                  <TableHead>{t("billing.mailCols.period")}</TableHead>
                  <TableHead>{t("billing.mailCols.recipients")}</TableHead>
                  <TableHead>{t("billing.mailCols.actor")}</TableHead>
                  <TableHead>{t("billing.mailCols.providerId")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {mailLog.map((m) => (
                  <TableRow key={m.id} className="row-hover" data-testid={`mail-row-${m.id}`}>
                    <TableCell className="font-mono text-[11px] text-muted-foreground">
                      {m.sent_at ? new Date(m.sent_at).toLocaleString() : new Date(m.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <StatusPill
                        kind={
                          m.status === "sent" ? "ok" : m.status === "pending" ? "warn" : "danger"
                        }
                      >
                        {m.status}
                      </StatusPill>
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {m.period_from} → {m.period_to}
                    </TableCell>
                    <TableCell className="text-xs">
                      {(m.recipients || []).join(", ")}
                    </TableCell>
                    <TableCell className="text-xs">
                      {m.actor}
                      <span className="text-muted-foreground"> ({m.actor_scope})</span>
                    </TableCell>
                    <TableCell className="font-mono text-[11px] text-muted-foreground">
                      {m.provider_id || m.error || "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
